import { RateLimitSettings } from '../../models/inventorySync';

/** Значения формы как ввёл пользователь — строки, чтобы не терять промежуточный ввод */
export type SettingsForm = Record<keyof RateLimitSettings, string>;

export interface SettingsField {
    key: keyof RateLimitSettings;
    label: string;
    /** Границы как в RateLimitSettings.Validate() на бэкенде — только для подсказки */
    bounds: string;
    hint: string;
}

export const SETTINGS_FIELDS: SettingsField[] = [
    {
        key: 'requestsPerMinute',
        label: 'Запросов в минуту (JSON API)',
        bounds: '1–20',
        hint: 'Темп запросов /inventory/ с одного IP: между запросами прокси пауза 60/RPM с (4 → 15 с). Замер: лимит Steam ~10/мин на IP, безопасно 4.',
    },
    {
        key: 'profilePageRequestsPerMinute',
        label: 'Запросов в минуту (HTML профиля)',
        bounds: 'пусто или 1–20',
        hint: 'Свой темп для HTML /profiles/.../inventory/ (проверка приватности, прогрев). Пусто — HTML делит паузы с JSON API.',
    },
    {
        key: 'requestJitterMs',
        label: 'Jitter паузы, мс',
        bounds: '0–10000',
        hint: 'Случайная добавка к паузе 0…N мс. Только удлиняет паузу, чтобы запросы с разных IP не шли синхронно.',
    },
    {
        key: 'cooldownStepsSeconds',
        label: 'Шаги cooldown, с',
        bounds: '1–5 шагов, каждый 10–900',
        hint: 'Отдых IP после 429: первый 429 — первый шаг, следующие подряд — эскалация. Через запятую, например 60, 120, 300.',
    },
    {
        key: 'interactiveMaxWaitSeconds',
        label: 'MaxWait интерактивных, с',
        bounds: '1–600',
        hint: 'Ордера, офферы, ручной синк: если оценка ожидания пула больше — запрос сразу уходит в fallback (steamapis/бот).',
    },
    {
        key: 'backgroundMaxWaitSeconds',
        label: 'MaxWait фоновых, с',
        bounds: '1–600',
        hint: 'Синк после логина, из админки, миграции: порог admission для фоновых запросов.',
    },
];

const LABELS = Object.fromEntries(SETTINGS_FIELDS.map(f => [f.key, f.label])) as Record<keyof RateLimitSettings, string>;

const INTEGER = /^-?\d+$/;

export function settingsToForm(settings: RateLimitSettings): SettingsForm {
    return {
        requestsPerMinute: String(settings.requestsPerMinute),
        profilePageRequestsPerMinute: settings.profilePageRequestsPerMinute == null ? '' : String(settings.profilePageRequestsPerMinute),
        requestJitterMs: String(settings.requestJitterMs),
        cooldownStepsSeconds: (settings.cooldownStepsSeconds ?? []).join(', '),
        interactiveMaxWaitSeconds: String(settings.interactiveMaxWaitSeconds),
        backgroundMaxWaitSeconds: String(settings.backgroundMaxWaitSeconds),
    };
}

/**
 * Форма → тело PUT. Проверяет только, что введены целые числа; границы проверяет сервер (400 с текстом ошибок),
 * чтобы правила не расходились с бэкендом.
 */
export function formToSettings(form: SettingsForm): { settings?: RateLimitSettings; errors: string[] } {
    const errors: string[] = [];
    const integer = (key: keyof RateLimitSettings): number => {
        const text = form[key].trim();
        if (!INTEGER.test(text)) {
            errors.push(`${LABELS[key]}: нужно целое число`);
            return 0;
        }
        return Number(text);
    };

    const requestsPerMinute = integer('requestsPerMinute');
    const profileText = form.profilePageRequestsPerMinute.trim();
    const profilePageRequestsPerMinute = profileText === '' ? null : integer('profilePageRequestsPerMinute');
    const requestJitterMs = integer('requestJitterMs');

    const steps = form.cooldownStepsSeconds.split(/[\s,;]+/).filter(s => s !== '');
    if (steps.length === 0 || !steps.every(s => INTEGER.test(s))) {
        errors.push(`${LABELS.cooldownStepsSeconds}: нужны целые числа через запятую`);
    }

    const interactiveMaxWaitSeconds = integer('interactiveMaxWaitSeconds');
    const backgroundMaxWaitSeconds = integer('backgroundMaxWaitSeconds');

    if (errors.length > 0) {
        return { errors };
    }
    return {
        settings: {
            requestsPerMinute,
            profilePageRequestsPerMinute,
            requestJitterMs,
            cooldownStepsSeconds: steps.map(Number),
            interactiveMaxWaitSeconds,
            backgroundMaxWaitSeconds,
        },
        errors,
    };
}

function displayValue(key: keyof RateLimitSettings, settings: RateLimitSettings): string {
    const value = settings[key];
    if (key === 'profilePageRequestsPerMinute' && value == null) {
        return 'общий';
    }
    return Array.isArray(value) ? value.join(', ') : String(value);
}

/** Изменённые поля «было → стало» для подтверждения сохранения */
export function diffSettings(before: RateLimitSettings, after: RateLimitSettings): string[] {
    return SETTINGS_FIELDS
        .map(f => ({ label: f.label, from: displayValue(f.key, before), to: displayValue(f.key, after) }))
        .filter(c => c.from !== c.to)
        .map(c => `${c.label}: ${c.from} → ${c.to}`);
}

/**
 * Текст ошибок из ответа PUT /settings: 400 {errors: string[]} от валидации лимитов,
 * ProblemDetails {errors: {поле: [..]}} от привязки модели, ProblemDetails {detail} для 503.
 */
export function parseSaveError(status: number, bodyText: string): string[] {
    let body: any;
    try {
        body = bodyText ? JSON.parse(bodyText) : undefined;
    } catch {
        body = undefined;
    }

    const errors = body?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
        return errors.map(String);
    }
    if (errors && typeof errors === 'object') {
        const flat = Object.entries(errors).flatMap(([field, messages]) =>
            (Array.isArray(messages) ? messages : [messages]).map(m => `${field}: ${m}`));
        if (flat.length > 0) {
            return flat;
        }
    }
    const detail = body?.detail ?? body?.title;
    if (typeof detail === 'string' && detail) {
        return [detail];
    }
    return [status >= 400 && status < 500 ? `Некорректный запрос (HTTP ${status})` : `Ошибка сервера (HTTP ${status})`];
}
